
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { MapPin, Star, MoreVertical, Eye, Trash2, Edit } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useNavigate } from 'react-router-dom';
import { useAuthSession } from '@/hooks/use-auth-session';
import { createPlaceUrl } from '@/lib/place-url-utils';

interface PlaylistPlaceCardProps {
  place: {
    id: string;
    name: string;
    city: string;
    country: string;
    place_type?: string;
    rating?: number;
    note?: string;
    photos?: string[];
    address?: string;
    created_by_user_id?: string;
  };
  isOwner?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onRemove?: (placeId: string) => void;
  onClick?: (place: any) => void;
  showActions?: boolean;
}

export const PlaylistPlaceCard = ({ 
  place, 
  isOwner = false,
  onEdit, 
  onDelete, 
  onRemove,
  onClick,
  showActions = true 
}: PlaylistPlaceCardProps) => {
  const navigate = useNavigate();
  const session = useAuthSession();
  const [imageError, setImageError] = useState(false);

  const handleCardClick = () => {
    if (onClick) {
      onClick(place);
    } else {
      navigate(createPlaceUrl(place));
    }
  };

  const handleRemove = () => {
    if (onRemove) {
      onRemove(place.id);
    } else if (onDelete) {
      onDelete();
    }
  };

  const shouldShowActions = showActions && (isOwner || session?.user?.id === place.created_by_user_id || onEdit || onDelete || onRemove);

  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group">
      <div onClick={handleCardClick}>
        {/* Image section */}
        <div className="relative h-48 bg-gray-200">
          {place.photos && place.photos.length > 0 && !imageError ? (
            <img
              src={place.photos[0]}
              alt={place.name}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <MapPin className="h-12 w-12 text-blue-400" />
            </div>
          )}
          
          {place.place_type && (
            <Badge className="absolute top-2 left-2 bg-white/90 text-black">
              {place.place_type}
            </Badge>
          )}
          
          {shouldShowActions && (
            <div className="absolute top-2 right-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                  <Button variant="ghost" size="sm" className="bg-white/90 hover:bg-white">
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleCardClick(); }}>
                    <Eye className="h-4 w-4 mr-2" />
                    View Details
                  </DropdownMenuItem>
                  {(isOwner || session?.user?.id === place.created_by_user_id || onEdit) && (
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit?.(); }}>
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                  )}
                  {(isOwner || session?.user?.id === place.created_by_user_id || onDelete || onRemove) && (
                    <DropdownMenuItem 
                      onClick={(e) => { e.stopPropagation(); handleRemove(); }}
                      className="text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      {onRemove ? 'Remove' : 'Delete'}
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>

        <CardContent className="p-4">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg group-hover:text-blue-600 transition-colors line-clamp-1">
              {place.name}
            </h3>
            
            <div className="flex items-center text-sm text-gray-600">
              <MapPin className="h-4 w-4 mr-1" />
              <span className="line-clamp-1">{place.city}, {place.country}</span>
            </div>
            
            {place.rating && (
              <div className="flex items-center">
                <Star className="h-4 w-4 text-yellow-400 fill-current mr-1" />
                <span className="text-sm font-medium">{place.rating}</span>
              </div>
            )}
            
            {place.note && (
              <p className="text-sm text-gray-600 line-clamp-2">
                {place.note}
              </p>
            )}
            
            {place.address && (
              <p className="text-xs text-gray-500 line-clamp-1">
                {place.address}
              </p>
            )}
          </div>
        </CardContent>
      </div>
    </Card>
  );
};
